import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mic, Pause, Play, Square, Loader2, Search, Check, Circle, Radio } from "lucide-react";
import { toast } from "sonner";

const fmt = (s) => {
  const n = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(n / 3600), m = Math.floor((n % 3600) / 60), ss = n % 60;
  const p = (x) => String(x).padStart(2, "0");
  return h > 0 ? `${p(h)}:${p(m)}:${p(ss)}` : `${p(m)}:${p(ss)}`;
};

const PROCESS_STEPS = [
  "Finalizing recording",
  "Creating transcript",
  "Identifying decisions",
  "Extracting action items",
  "Preparing summary",
];

/**
 * MeetingRecorder — simple Record → transcribe → summarize → post-to-chat flow.
 * Reuses the backend calls/Whisper/summary pipeline (a meeting is a call doc).
 */
export default function MeetingRecorder({ open, onOpenChange, defaultChatId = null, onPosted }) {
  const [view, setView] = useState("setup"); // setup | recording | processing | done
  const [title, setTitle] = useState("");
  const [chats, setChats] = useState([]);
  const [chatId, setChatId] = useState(defaultChatId);
  const [search, setSearch] = useState("");
  const [meetingId, setMeetingId] = useState(null);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [procStep, setProcStep] = useState(0);
  const [postedChat, setPostedChat] = useState(null);
  const [liveTranscript, setLiveTranscript] = useState([]);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunkTimerRef = useRef(null);
  const seqRef = useRef(0);
  const pausedRef = useRef(false);
  const stoppedRef = useRef(false);
  const meetingIdRef = useRef(null);
  const transcriptScrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setView("setup"); setTitle(""); setChatId(defaultChatId); setSearch("");
    setMeetingId(null); setPaused(false); setElapsed(0); setProcStep(0); setPostedChat(null);
    setLiveTranscript([]);
    seqRef.current = 0; pausedRef.current = false; stoppedRef.current = false; meetingIdRef.current = null;
    api.get("/chats").then(({ data }) => setChats(data || [])).catch(() => setChats([]));
  }, [open, defaultChatId]);

  useEffect(() => () => cleanupStream(), []);

  const cleanupStream = () => {
    stoppedRef.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (chunkTimerRef.current) clearTimeout(chunkTimerRef.current);
    chunkTimerRef.current = null;
    try { mediaRef.current?.stop(); } catch { /* noop */ }
    mediaRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  };

  const CHUNK_MS = 4000;
  const pickMime = () =>
    (window.MediaRecorder?.isTypeSupported?.("audio/webm;codecs=opus") && "audio/webm;codecs=opus") ||
    (window.MediaRecorder?.isTypeSupported?.("audio/webm") && "audio/webm") || "";

  const uploadChunk = async (blob) => {
    if (!blob || blob.size < 4096 || !meetingIdRef.current) return;
    chunksRef.current.push(blob);
    const fd = new FormData();
    fd.append("seq", String(seqRef.current++));
    fd.append("file", blob, "chunk.webm");
    try {
      const { data } = await api.post(`/meetings/record/${meetingIdRef.current}/transcribe-chunk`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.text) {
        setLiveTranscript((p) => [...p, { id: data.segment?.id || `${Date.now()}-${p.length}`, text: data.text }]);
        requestAnimationFrame(() => {
          const el = transcriptScrollRef.current;
          if (el) el.scrollTop = el.scrollHeight;
        });
      }
    } catch { /* transient chunk failure — keep recording */ }
  };

  // Rotating MediaRecorder: each ~4s slice is a standalone, decodable webm file
  // so Whisper can transcribe it independently (live transcript).
  const runRecorderCycle = () => {
    if (stoppedRef.current || pausedRef.current || !streamRef.current) return;
    const mime = pickMime();
    let buf = [];
    let rec;
    try {
      rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    } catch { return; }
    rec.ondataavailable = (ev) => { if (ev.data?.size) buf.push(ev.data); };
    rec.onstop = () => {
      const blob = new Blob(buf, { type: mime || "audio/webm" });
      if (!pausedRef.current) uploadChunk(blob);
      if (!stoppedRef.current && !pausedRef.current) runRecorderCycle();
    };
    mediaRef.current = rec;
    rec.start();
    chunkTimerRef.current = setTimeout(() => { try { rec.stop(); } catch { /* noop */ } }, CHUNK_MS);
  };

  const start = async () => {
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error("Microphone permission is needed to record a meeting.");
      return;
    }
    streamRef.current = stream;
    try {
      const { data } = await api.post("/meetings/record/start", {
        title: title.trim() || null,
        destination_chat_id: chatId || null,
      });
      setMeetingId(data.meeting.id);
      meetingIdRef.current = data.meeting.id;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not start the meeting");
      cleanupStream();
      return;
    }
    chunksRef.current = [];
    seqRef.current = 0;
    stoppedRef.current = false;
    pausedRef.current = false;
    setLiveTranscript([]);
    setView("recording");
    setPaused(false);
    setElapsed(0);
    startTimer();
    runRecorderCycle();
  };

  const pause = async () => {
    pausedRef.current = true;
    if (chunkTimerRef.current) clearTimeout(chunkTimerRef.current);
    try { mediaRef.current?.stop(); } catch { /* noop */ }
    if (timerRef.current) clearInterval(timerRef.current);
    setPaused(true);
    if (meetingIdRef.current) api.post(`/meetings/record/${meetingIdRef.current}/pause`).catch(() => {});
  };

  const resume = async () => {
    pausedRef.current = false;
    startTimer();
    setPaused(false);
    if (meetingIdRef.current) api.post(`/meetings/record/${meetingIdRef.current}/resume`).catch(() => {});
    runRecorderCycle();
  };

  const stop = async () => {
    const hadLive = liveTranscript.length > 0;
    // capture the trailing slice before tearing down
    const mr = mediaRef.current;
    const tail = await new Promise((resolve) => {
      if (!mr || mr.state === "inactive") return resolve(null);
      const prevOnStop = mr.onstop;
      mr.onstop = (e) => { if (prevOnStop) { /* skip auto-restart */ } resolve(null); };
      try { mr.stop(); } catch { resolve(null); }
      setTimeout(() => resolve(null), 800);
    });
    void tail;
    cleanupStream();
    setView("processing");
    setProcStep(0);
    const stepIv = setInterval(() => setProcStep((s) => Math.min(s + 1, PROCESS_STEPS.length - 1)), 1400);
    try {
      await api.post(`/meetings/record/${meetingIdRef.current}/stop`);
      // If live transcription produced nothing, fall back to a full-file upload
      // so finalize still has audio to transcribe. Otherwise skip (avoids
      // double transcription + double credit charge).
      if (!hadLive && chunksRef.current.length > 0) {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size > 0) {
          const fd = new FormData();
          fd.append("file", blob, "meeting.webm");
          await api.post(`/calls/${meetingIdRef.current}/upload-recording`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
      }
      const { data } = await api.post(`/meetings/record/${meetingIdRef.current}/finalize`, {});
      const c = chats.find((x) => x.id === (chatId || data.meeting.destination_chat_id));
      setPostedChat(c || null);
      clearInterval(stepIv);
      setView("done");
      onPosted?.(data);
    } catch (e) {
      clearInterval(stepIv);
      toast.error(e?.response?.data?.detail?.message || "Could not process the meeting");
      setView("done");
    }
  };

  const filtered = chats.filter((c) => (c.name || "").toLowerCase().includes(search.toLowerCase()));
  const selectedChat = chats.find((c) => c.id === chatId);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) cleanupStream(); onOpenChange(v); }}>
      <DialogContent className="bg-surface border-hairline max-w-md p-0 overflow-hidden" data-testid="meeting-recorder">
        <DialogTitle className="sr-only">Meeting Recorder</DialogTitle>
        <DialogDescription className="sr-only">Record a meeting, then auto-transcribe and summarize it into a chat.</DialogDescription>
        <div className="px-5 py-4 border-b border-hairline flex items-center gap-2">
          <Radio className="w-4 h-4 text-brand" />
          <span className="text-[15px] font-bold text-ink">Meeting Recorder</span>
        </div>

        {view === "setup" && (
          <div className="p-5 space-y-4" data-testid="recorder-setup">
            <input
              data-testid="recorder-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Meeting title (optional)"
              className="w-full bg-surface2 border border-hairline rounded-xl px-3 py-2.5 text-[14px] text-ink placeholder:text-ink-mute outline-none focus:border-brand/50"
            />
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-mute mb-1.5">Save meeting to</div>
              {selectedChat ? (
                <button data-testid="recorder-chat-selected" onClick={() => setChatId(null)}
                  className="w-full flex items-center justify-between border border-brand/40 bg-brand/10 rounded-xl px-3 py-2.5 text-[14px] text-ink">
                  <span className="truncate">{selectedChat.name || "Chat"}</span>
                  <Check className="w-4 h-4 text-brand" />
                </button>
              ) : (
                <>
                  <div className="flex items-center gap-2 bg-surface2 border border-hairline rounded-xl px-3 py-2 mb-1.5">
                    <Search className="w-4 h-4 text-ink-mute" />
                    <input data-testid="recorder-chat-search" value={search} onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search chats…" className="flex-1 bg-transparent text-[14px] text-ink placeholder:text-ink-mute outline-none" />
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-0.5">
                    {filtered.slice(0, 30).map((c) => (
                      <button key={c.id} data-testid={`recorder-chat-${c.id}`} onClick={() => setChatId(c.id)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/5 text-[14px] text-ink flex items-center gap-2">
                        <span className="truncate flex-1">{c.name || "Chat"}</span>
                        {c.kind === "project" && <span className="text-[9px] font-bold text-brand">PROJECT</span>}
                      </button>
                    ))}
                    {filtered.length === 0 && <div className="px-3 py-3 text-[13px] text-ink-mute">No chats found</div>}
                  </div>
                </>
              )}
              <p className="text-[11px] text-ink-mute mt-2">You can also choose a chat after recording. The summary posts there automatically.</p>
            </div>
            <p className="text-[11px] text-ink-mute leading-relaxed border border-hairline rounded-lg px-3 py-2 bg-surface2">
              Make sure everyone knows this meeting is being recorded.
            </p>
            <button data-testid="recorder-start-btn" onClick={start}
              className="w-full bg-brand text-black rounded-xl py-3 font-bold text-[15px] flex items-center justify-center gap-2 hover:brightness-95">
              <Mic className="w-4 h-4" /> Start recording
            </button>
          </div>
        )}

        {view === "recording" && (
          <div className="p-6 flex flex-col items-center gap-5" data-testid="recorder-live">
            <div className="flex items-center gap-2 text-[13px] font-bold" style={{ color: paused ? "var(--ink-mute)" : "#ef4444" }}>
              <Circle className={`w-3 h-3 ${paused ? "" : "animate-pulse"}`} fill="currentColor" />
              {paused ? "PAUSED" : "RECORDING"}
            </div>
            <div className="text-[44px] font-bold tabular-nums text-ink tracking-tight" data-testid="recorder-timer">{fmt(elapsed)}</div>
            <div className="flex items-center gap-1 h-10">
              {Array.from({ length: 24 }).map((_, i) => (
                <span key={i} className="w-1 rounded-full bg-brand/70"
                  style={{ height: paused ? 6 : 6 + Math.abs(Math.sin((Date.now() / 220) + i)) * 28,
                           transition: "height 120ms ease" }} />
              ))}
            </div>
            <div className="flex items-center gap-3 w-full">
              {paused ? (
                <button data-testid="recorder-resume-btn" onClick={resume} className="flex-1 bg-surface2 border border-hairline rounded-xl py-3 font-semibold text-ink flex items-center justify-center gap-2">
                  <Play className="w-4 h-4" /> Resume
                </button>
              ) : (
                <button data-testid="recorder-pause-btn" onClick={pause} className="flex-1 bg-surface2 border border-hairline rounded-xl py-3 font-semibold text-ink flex items-center justify-center gap-2">
                  <Pause className="w-4 h-4" /> Pause
                </button>
              )}
              <button data-testid="recorder-stop-btn" onClick={stop} className="flex-1 bg-red-500 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2">
                <Square className="w-4 h-4" fill="currentColor" /> Stop
              </button>
            </div>

            <div className="w-full border border-hairline rounded-xl bg-surface2 overflow-hidden" data-testid="recorder-live-transcript">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-hairline">
                <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-mute">Live transcript</span>
              </div>
              <div ref={transcriptScrollRef} className="max-h-40 overflow-y-auto px-3 py-2.5 space-y-1.5">
                {liveTranscript.length === 0 ? (
                  <p className="text-[12px] text-ink-mute italic">Listening… your words will appear here as you speak.</p>
                ) : (
                  liveTranscript.map((seg) => (
                    <p key={seg.id} className="text-[13px] text-ink-dim leading-snug" data-testid="live-transcript-line">{seg.text}</p>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {view === "processing" && (
          <div className="p-6 space-y-3" data-testid="recorder-processing">
            <div className="flex items-center gap-2 text-ink font-semibold text-[15px] mb-2">
              <Loader2 className="w-4 h-4 animate-spin text-brand" /> Turning your meeting into notes…
            </div>
            {PROCESS_STEPS.map((s, i) => (
              <div key={s} className={`flex items-center gap-2 text-[13px] ${i <= procStep ? "text-ink" : "text-ink-mute"}`}>
                {i < procStep ? <Check className="w-3.5 h-3.5 text-tn-green" /> : i === procStep ? <Loader2 className="w-3.5 h-3.5 animate-spin text-brand" /> : <Circle className="w-3.5 h-3.5 opacity-30" />}
                {s}
              </div>
            ))}
          </div>
        )}

        {view === "done" && (
          <div className="p-6 text-center space-y-4" data-testid="recorder-done">
            <div className="w-14 h-14 rounded-full bg-tn-green/15 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-tn-green" />
            </div>
            <div>
              <div className="text-[16px] font-bold text-ink">Meeting summary ready</div>
              <div className="text-[13px] text-ink-dim mt-1">
                {postedChat ? `Posted to ${postedChat.name || "your chat"}.` : "Your meeting was processed."}
              </div>
            </div>
            <button data-testid="recorder-done-btn" onClick={() => onOpenChange(false)}
              className="w-full bg-brand text-black rounded-xl py-3 font-bold">Done</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
